import { ItemTypes, variableHandlingType } from '../constants/constants';
import { orderBoxesByContainer } from './boxes';
import { getTopLevelContainer } from './columns';

// variableOrderArray est indexé sur les containers top-level : une box dans une
// ligne imbriquée est rattachée au container qui héberge cette ligne.
const findBoxContainer = (draft, columnId) =>
  getTopLevelContainer({
    columnId,
    containers: draft.containers,
    rowContainers: draft.rowContainers,
  });

const findBoxContainerIdx = (draft, columnId) => {
  const container = findBoxContainer(draft, columnId);
  return container
    ? draft.containers.findIndex((c) => c.id === container.id)
    : -1;
};

export const mutateInsertVariableBox = (draft, box) => {
  if (
    box.from_linked_section &&
    draft.variableOrderArray.some(({ id }) => id === box.id)
  ) {
    if (box.columnId) {
      const boxContainerIdx = findBoxContainerIdx(draft, box.columnId);
      const vOrderIdx = draft.variableOrderArray.findIndex(
        ({ id }) => id === box.id
      );
      if (boxContainerIdx !== -1) {
        draft.variableOrderArray[vOrderIdx].containerId =
          draft.containers[boxContainerIdx].id;
      }
    }
    return;
  }
  if (box.boxSectionId) return;
  if (!box.columnId) {
    draft.variableOrderArray.unshift({
      id: box.id,
      containerId: box.id,
    });
    return;
  }
  const boxContainerIdx = findBoxContainerIdx(draft, box.columnId);
  if (boxContainerIdx === -1) return;
  const afterContainer = draft.containers.find(
    (c, idx) =>
      boxContainerIdx <= idx &&
      draft.variableOrderArray.some(({ containerId }) => containerId === c.id)
  );
  if (afterContainer) {
    const afterOrderIdx = draft.variableOrderArray.findIndex(
      ({ containerId }) => containerId === afterContainer.id
    );
    draft.variableOrderArray = [
      ...draft.variableOrderArray.slice(0, afterOrderIdx),
      { id: box.id, containerId: draft.containers[boxContainerIdx].id },
      ...draft.variableOrderArray.slice(afterOrderIdx),
    ];
  } else {
    draft.variableOrderArray.push({
      id: box.id,
      containerId: draft.containers[boxContainerIdx].id,
    });
  }
};

export const mutateTakeOutItem = (draft, vId, parentId) => {
  const idx = draft.variableOrderArray.findIndex(
    ({ id, parentId: vParentId }) =>
      id === vId && (!parentId || parentId === vParentId)
  );
  if (idx !== -1) draft.variableOrderArray.splice(idx, 1);
};

export const mutateRemoveUnusedVariableGroup = (draft) => {
  draft.variableGroups.forEach((vG) => {
    const vGIsUsed = vG.variableIds.some((id) => !draft.variables[id]?.notUsed);
    if (!vGIsUsed) {
      mutateTakeOutItem(draft, vG.id);
    }
  });
};

const getIdAndContainerIdFromSection = ({
  boxesToAdd,
  id,
  containerId,
  draft,
}) => {
  if (boxesToAdd.some(({ boxSectionId }) => boxSectionId === id)) {
    const b = boxesToAdd.find(({ boxSectionId }) => boxSectionId === id);
    if (b.columnId) {
      const boxContainerIdx = findBoxContainerIdx(draft, b.columnId);
      if (boxContainerIdx !== -1) {
        return { id: b.id, containerId: draft.containers[boxContainerIdx].id };
      }
    }
    return { id: b.id, containerId: b.id };
  }
  return { id, containerId };
};

export const mutateSortVariableFromSection = ({
  draft,
  sectionContent,
  boxesToAdd,
  containerIdx,
  parentContainerId,
  parentId,
  isDocumentLoading,
}) => {
  const afterContainer = draft.containers.find(
    (c, idx) =>
      c.id !== parentContainerId &&
      containerIdx <= idx &&
      draft.variableOrderArray.some(({ containerId }) => containerId === c.id)
  );
  if (afterContainer) {
    let afterOrderIdx = draft.variableOrderArray.findIndex(
      ({ containerId }) => containerId === afterContainer.id
    );
    while (
      afterOrderIdx > 0 &&
      draft.boxes.find(
        ({ id }) =>
          id === draft.variableOrderArray[afterOrderIdx - 1].id &&
          !boxesToAdd.some(({ id: bId }) => id === bId)
      )?.type === ItemTypes.QUESTION
    ) {
      afterOrderIdx -= 1;
    }
    let firstIdxVariable = draft.variableOrderArray.findIndex(
      ({ id }) => id === boxesToAdd.some(({ id: bId }) => id === bId)
    );
    firstIdxVariable = firstIdxVariable === -1 ? 0 : firstIdxVariable;
    const sectionDiff = afterOrderIdx - firstIdxVariable;
    sectionContent.variableOrderArray
      .filter(({ id }) => {
        if (
          draft.currentDocument.type !== 'template' &&
          !isDocumentLoading &&
          !draft.variableOrderArray.some(
            ({ id: vId, parentId: vParentId }) =>
              id === vId && parentId === vParentId
          ) &&
          (!!draft.variables[id] ||
            draft.variableGroups.some((vG) => vG.id === id))
        )
          return true;
        const idToUse =
          boxesToAdd.find(({ boxSectionId }) => boxSectionId === id)?.id ?? id;
        return !draft.variableOrderArray.some(({ id: vId }) => idToUse === vId);
      })
      .forEach(({ id: vId, containerId: vContainerId }, idx) => {
        const { id, containerId } = getIdAndContainerIdFromSection({
          boxesToAdd,
          id: vId,
          containerId: vContainerId,
          draft,
        });
        draft.variableOrderArray = [
          ...draft.variableOrderArray.slice(0, sectionDiff + idx),
          { id, containerId, parentId },
          ...draft.variableOrderArray.slice(sectionDiff + idx),
        ];
      });
  } else {
    sectionContent.variableOrderArray.forEach(({ id, containerId }) => {
      if (
        !draft.variableOrderArray.some(({ id: vId }) => id === vId) ||
        (draft.currentDocument.type !== 'template' &&
          !isDocumentLoading &&
          !draft.variableOrderArray.some(
            ({ id: vId, parentId: vParentId }) =>
              id === vId && parentId === vParentId
          ) &&
          (!!draft.variables[id] ||
            draft.variableGroups.some((vG) => vG.id === id)))
      ) {
        const { id: realId, containerId: realContainerId } =
          getIdAndContainerIdFromSection({
            boxesToAdd,
            id: id,
            containerId: containerId,
            draft,
          });
        draft.variableOrderArray.push({
          id: realId,
          containerId: realContainerId,
          parentId,
        });
      }
    });
  }
};

export const mutateInsertVariableInOrderArray = (draft, variableKey) => {
  if (draft.variableGroups.some((vG) => vG.variableIds.includes(variableKey))) {
    const vG = draft.variableGroups.find((vG) =>
      vG.variableIds.includes(variableKey)
    );
    if (!draft.variableOrderArray.some(({ id }) => id === vG.id)) {
      draft.variableOrderArray.unshift({ id: vG.id, containerId: vG.id });
      return true;
    }
  } else {
    if (!draft.variableOrderArray.some(({ id }) => id === variableKey)) {
      draft.variableOrderArray.unshift({
        id: variableKey,
        containerId: variableKey,
      });
      return true;
    }
  }
  return false;
};

export const sortAndFilterVariablesGroupedByContainer = ({
  variablesGroupedByContainer,
  variableOrderArray,
  filterHiddenBoxes = false,
}) => {
  let variablesToUse = variablesGroupedByContainer.filter((vInfos) => {
    if (vInfos.type === variableHandlingType.FORM)
      return (
        !filterHiddenBoxes ||
        vInfos.variablesBoxes.some(({ hideInForm }) => !hideInForm)
      );
    if (vInfos.variableGroupBox)
      return (
        vInfos.variablesBoxes.length > 0 &&
        vInfos.variablesBoxes.some((v) => !v.notUsed) &&
        (!filterHiddenBoxes ||
          vInfos.variablesBoxes.some(({ hideInForm }) => !hideInForm))
      );
    return (
      !vInfos.variablesBoxes[0].notUsed &&
      (!filterHiddenBoxes || !vInfos.variablesBoxes[0].hideInForm)
    );
  });
  if (filterHiddenBoxes) {
    variablesToUse = variablesToUse.map((v) => {
      return {
        ...v,
        variablesBoxes: v.variablesBoxes.filter(
          ({ hideInForm }) => !hideInForm
        ),
      };
    });
  }
  
  // Créer des Maps pour les lookups O(1) au lieu de find() O(n)
  const variablesByGroupId = new Map();
  const variablesByFirstBoxId = new Map();
  
  variablesToUse.forEach((vInfos) => {
    if (vInfos.variableGroupBox) {
      variablesByGroupId.set(vInfos.variableGroupBox.id, vInfos);
    } else if (vInfos.variablesBoxes && vInfos.variablesBoxes.length > 0) {
      variablesByFirstBoxId.set(vInfos.variablesBoxes[0].id, vInfos);
    }
  });
  
  const array = variableOrderArray
    .map(({ id }) => {
      // Chercher d'abord dans les groupements, puis dans les variables individuelles
      return variablesByGroupId.get(id) || variablesByFirstBoxId.get(id);
    })
    .filter((obj) => obj !== undefined);

  // Créer un Set des IDs déjà dans array pour des lookups O(1)
  const arrayContainerIds = new Set(
    array.map((item) => item.variableGroupBox?.id || item.variablesBoxes[0]?.id).filter(Boolean)
  );
  const arrayFirstBoxIds = new Set(
    array
      .filter((item) => !item.variableGroupBox)
      .map((item) => item.variablesBoxes[0]?.id)
      .filter(Boolean)
  );

  // Récupérer les items de type FORM qui ne sont pas dans variableOrderArray
  // Ces items (comme les questions avec onlyForm: true) doivent être inclus dans le résultat final
  const formItemsNotInOrder = variablesToUse.filter((vInfos) => {
    if (vInfos.type !== variableHandlingType.FORM) return false;
    const firstBoxId = vInfos.variablesBoxes[0]?.id;
    return firstBoxId && !arrayFirstBoxIds.has(firstBoxId);
  });

  return variablesToUse
    .filter((vInfos) => {
      if (vInfos.type === variableHandlingType.FORM) return false;
      if (vInfos.variableGroupBox) {
        return !arrayContainerIds.has(vInfos.variableGroupBox.id);
      }
      const firstBoxId = vInfos.variablesBoxes[0]?.id;
      return firstBoxId && !arrayContainerIds.has(firstBoxId);
    })
    .concat(array)
    .concat(formItemsNotInOrder);
};

const reorderArray = (referenceArray, arrayToReorder) => {
  return referenceArray
    .map((id) => arrayToReorder.find((el) => el.id === id))
    .filter((el) => el !== undefined);
};

export const sortVariableOrderArray = (draft) => {
  // Filtrer les boxes de variables
  const variableBoxes = draft.boxes.filter(
    (box) =>
      box.type === ItemTypes.TEXT_VARIABLE ||
      box.type === ItemTypes.CHECKBOXS_VARIABLE ||
      box.type === ItemTypes.QUESTION ||
      box.type === ItemTypes.CUSTOM_TEXT_VARIABLE ||
      box.type === ItemTypes.VARIABLE_GROUP ||
      box.type === ItemTypes.SECTION_VARIABLE ||
      box.type === ItemTypes.SELECTOR_VARIABLE ||
      box.type === ItemTypes.IMG_VARIABLE ||
      box.type === ItemTypes.TABLE_VARIABLE ||
      box.type === ItemTypes.FILE_PDF_VARIABLE
  );

  // Séparer les boxes avec et sans columnId (les questions avec onlyForm: true n'ont pas de columnId)
  const boxesWithColumnId = variableBoxes.filter((box) => box.columnId);
  const boxesWithoutColumnId = variableBoxes.filter((box) => !box.columnId);

  // Ordonner les boxes avec columnId selon leur position visuelle dans le document
  const orderedBoxesWithColumnId = orderBoxesByContainer({
    containers: draft.containers,
    boxesToSort: [...boxesWithColumnId],
  });

  const variableOrderArray = draft.variableOrderArray;

  // Créer un Map pour accéder rapidement aux éléments de variableOrderArray par ID
  const variableOrderMap = new Map(
    variableOrderArray.map((item) => [item.id, item])
  );

  // Créer un Map pour les boxes avec columnId triés par position visuelle
  const orderedBoxesWithColumnIdMap = new Map(
    orderedBoxesWithColumnId.map((box) => [box.id, box])
  );

  // Construire le nouveau tableau en préservant l'ordre relatif de variableOrderArray
  // Pour les boxes avec columnId, utiliser l'ordre visuel mais préserver leur position relative
  // Pour les boxes sans columnId (questions), préserver leur position dans variableOrderArray
  const newVariableOrderArray = [];
  const processedBoxIds = new Set();

  // Parcourir variableOrderArray pour préserver l'ordre relatif
  variableOrderArray.forEach((item) => {
    // Vérifier si c'est un box avec columnId
    const boxWithColumnId = boxesWithColumnId.find((b) => b.id === item.id);
    if (boxWithColumnId) {
      // C'est un box avec columnId, utiliser l'ordre visuel
      const orderedBox = orderedBoxesWithColumnIdMap.get(boxWithColumnId.id);
      if (orderedBox) {
        // Le box est dans l'ordre visuel, utiliser l'item existant pour préserver les propriétés
        newVariableOrderArray.push(item);
        processedBoxIds.add(item.id);
      } else {
        // Le box n'est pas dans l'ordre visuel (ne devrait pas arriver), l'ajouter quand même
        const boxContainerId = findBoxContainer(
          draft,
          boxWithColumnId.columnId
        )?.id;
        if (boxContainerId) {
          newVariableOrderArray.push({
            id: item.id,
            containerId: boxContainerId,
            ...(item.parentId && { parentId: item.parentId }),
          });
          processedBoxIds.add(item.id);
        }
      }
      return;
    }

    // Vérifier si c'est un box sans columnId (question)
    const boxWithoutColumnId = boxesWithoutColumnId.find(
      (b) => b.id === item.id
    );
    if (boxWithoutColumnId) {
      // C'est une question ou un box sans columnId, préserver sa position
      newVariableOrderArray.push(item);
      processedBoxIds.add(item.id);
      return;
    }

    // L'item n'est pas un box (peut-être une variable group ou autre)
    // Vérifier s'il existe toujours dans le draft
    const variableGroup = draft.variableGroups.find((vg) => vg.id === item.id);
    const variable = draft.variables[item.id];
    if (variableGroup || variable) {
      newVariableOrderArray.push(item);
      processedBoxIds.add(item.id);
    }
  });

  // Ajouter les boxes avec columnId qui ne sont pas encore dans variableOrderArray
  // (dans l'ordre visuel)
  orderedBoxesWithColumnId.forEach((box) => {
    if (!processedBoxIds.has(box.id)) {
      const boxContainerId = findBoxContainer(draft, box.columnId)?.id;
      if (boxContainerId) {
        newVariableOrderArray.push({
          id: box.id,
          containerId: boxContainerId,
        });
        processedBoxIds.add(box.id);
      }
    }
  });

  // Ajouter les boxes sans columnId qui ne sont pas encore dans variableOrderArray
  boxesWithoutColumnId.forEach((box) => {
    if (!processedBoxIds.has(box.id)) {
      newVariableOrderArray.push({
        id: box.id,
        containerId: box.id,
      });
    }
  });

  draft.variableOrderArray = newVariableOrderArray;
};

export const removeDuplicatesInVariableOrderArray = (variableOrderArray) => {
  return Object.values(
    variableOrderArray.reduce((acc, item) => {
      const key = JSON.stringify({
        id: item.id,
        containerId: item.containerId,
      });

      // If we haven't seen this combination before, add it
      if (!acc[key]) {
        acc[key] = item;
        return acc;
      }

      // If current item doesn't have parentId and existing one does, replace it
      if (!item.parentId && acc[key].parentId) {
        acc[key] = item;
      }

      return acc;
    }, {})
  );
};
